import React from "react";
import { motion } from "framer-motion";
import useTheme from "../../hooks/useTheme";
// adjust path if your hook is elsewhere (e.g., src/utils/useTheme or src/hooks/useTheme)

// adjust path if ShimmerEffect is under src/components/ or src/components/common/

const ShimmerEffect = () => (
  <motion.div
    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
    animate={{
      x: ["-100%", "100%"],
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      repeatType: "loop",
      ease: "linear",
    }}
  />
);


const StatCardSkeleton = () => {
  const theme = useTheme();

  return (
    <Card className="p-6 max-sm:p-4" gradient>
      <ShimmerEffect />
      <div className="flex items-start justify-between relative z-10 max-sm:items-center">
        <div className="space-y-3 flex-1 max-sm:space-y-2">
          <div
            className={`h-4 w-24 rounded-lg max-sm:h-3 max-sm:w-20 ${
              theme.darkMode ? "bg-gray-800" : "bg-gray-200"
            } animate-pulse`}
          />
          <div
            className={`h-8 w-20 rounded-lg max-sm:h-6 max-sm:w-16 ${
              theme.darkMode ? "bg-gray-800" : "bg-gray-200"
            } animate-pulse`}
          />
        </div>
        <div
          className={`w-14 h-14 rounded-xl max-sm:w-10 max-sm:h-10 max-sm:rounded-lg ${
            theme.darkMode ? "bg-gray-800" : "bg-gray-200"
          } animate-pulse`}
        />
      </div>
    </Card>
  );
};

const CardSkeleton = ({ className = "" }) => {
  const theme = useTheme();

  return (
    <div
      className={`relative rounded-2xl border overflow-hidden max-sm:rounded-xl ${
        theme.darkMode
          ? "bg-gray-900/50 border-gray-800"
          : "bg-white border-gray-200"
      } p-6 max-sm:p-4 ${className}`}
    >
      <ShimmerEffect />
      <div className="space-y-4 relative z-10 max-sm:space-y-3">
        <div className="flex items-center justify-between max-sm:items-start">
          <div className="space-y-3 flex-1 max-sm:space-y-2">
            <div
              className={`h-6 w-40 rounded-lg max-sm:h-5 max-sm:w-32 ${
                theme.darkMode ? "bg-gray-800" : "bg-gray-200"
              } animate-pulse`}
            />
            <div
              className={`h-4 w-32 rounded-lg max-sm:h-3 max-sm:w-24 ${
                theme.darkMode ? "bg-gray-800/70" : "bg-gray-200/70"
              } animate-pulse`}
            />
          </div>
          <div
            className={`h-14 w-14 rounded-xl max-sm:h-10 max-sm:w-10 max-sm:rounded-lg ${
              theme.darkMode ? "bg-gray-800" : "bg-gray-200"
            } animate-pulse`}
          />
        </div>
        <div className="space-y-3 pt-4 max-sm:pt-3 max-sm:space-y-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center justify-between max-sm:flex-col max-sm:items-start max-sm:gap-1">
              <div className="flex items-center gap-3 flex-1 max-sm:gap-2 max-sm:w-full">
                <div
                  className={`w-4 h-4 rounded-full max-sm:w-3 max-sm:h-3 ${
                    theme.darkMode ? "bg-gray-800" : "bg-gray-200"
                  } animate-pulse`}
                />
                <div
                  className={`h-4 w-28 rounded-lg max-sm:h-3 max-sm:w-20 ${
                    theme.darkMode ? "bg-gray-800/70" : "bg-gray-200/70"
                  } animate-pulse`}
                />
              </div>
              <div
                className={`h-4 w-32 rounded-lg max-sm:h-3 max-sm:w-28 max-sm:ml-6 ${
                  theme.darkMode ? "bg-gray-800/70" : "bg-gray-200/70"
                } animate-pulse`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Card = ({
  children,
  className = "",
  hover = false,
  gradient = false,
  ...props
}) => {
  const theme = useTheme();
  if(children === undefined || children === null){
    return null;
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`
        relative rounded-2xl border backdrop-blur-xl transition-all duration-300
        ${
          gradient
            ? theme.darkMode
              ? "bg-gradient-to-br from-gray-900/90 to-gray-800/50 border-gray-700/50"
              : "bg-gradient-to-br from-white to-gray-50 border-gray-200/50"
            : theme.colors.bg.card + " " + theme.colors.border.primary
        }
        ${
          hover
            ? theme.colors.bg.hover +
              " shadow-lg hover:shadow-md hover:scale-[1.01]"
            : ""
        }
        ${className}
      `}
      {...props}
    >
      {gradient && (
        <div
          className={`absolute inset-0 ${
            theme.darkMode
              ? "bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"
              : "bg-gradient-to-br from-blue-500/3 via-purple-500/3 to-pink-500/3"
          } rounded-2xl`}
        />
      )}
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
};

export default Card;
export { CardSkeleton, StatCardSkeleton };